/**
 * Delivery statuses that still allow the bultos of an order to be added,
 * removed or re-counted.
 *
 * Past these the courier already holds the parcels and their labels are
 * printed, so changing the list would desynchronise us from them.
 */
export const EDITABLE_ITEM_STATUSES = [
  'pending',
  'created_on_courier',
  'waiting_for_assignment',
  'fulfilled',
  'packed',
];

/**
 * Statuses in which the order is waiting to be handed over, which is when the
 * logistics of each bulto (weight and dimensions) are captured and the parcel
 * can be pushed to the courier.
 */
export const AWAITING_DISPATCH_STATUSES = ['waiting_for_assignment', 'fulfilled', 'packed'];

/**
 * Couriers that print one label per order instead of one per bulto, so the
 * label column offers the whole set rather than a single sheet.
 */
export const MULTI_LABEL_COURIERS = [
  'fedex',
  'bluex',
  'startken',
  'uberdirect',
  'shippify',
  'packet',
];

/**
 * Whether a bulto carries every measurement the courier needs to quote it.
 *
 * All four have to be present: a parcel with a weight but no dimensions cannot
 * be priced, so it counts as pending just the same.
 *
 * @param {object} item
 * @returns {boolean}
 */
export const itemHasLogistics = (item) =>
  item?.weight_grams > 0 &&
  item?.height_cm > 0 &&
  item?.width_cm > 0 &&
  item?.length_cm > 0;

/**
 * Total weight of the order, in grams, taken from its bultos.
 *
 * The backend treats the items as the reference for everything, which makes
 * `delivery.weight` a derived field that goes stale — orders exist today whose
 * delivery says 500 g while their bultos add up to 2000 g. Anything showing a
 * weight reads it from here so the summary card and the bultos KPI cannot
 * disagree.
 *
 * @param {Array<object>} items
 * @returns {number} Zero when no bulto has been weighed yet.
 */
export const sumItemWeights = (items = []) =>
  items.reduce((total, item) => total + (item.weight_grams || 0), 0);

/**
 * Total volume of the order, in cubic centimetres.
 *
 * @param {Array<object>} items
 * @returns {number}
 */
export const sumItemVolumes = (items = []) =>
  items.reduce(
    (total, item) =>
      total + (item.height_cm || 0) * (item.width_cm || 0) * (item.length_cm || 0),
    0
  );
